import { defineStore } from "pinia";
import { computed, ref } from "vue";

import type { ALMIssue } from "@/backend/natsstubs/almsync/common/models";
import { fetchAlmIssues } from "@/backend/read/almIssues";
import { captureException } from "@/error/sentry";
import type { Color } from "@/model/baseTypes";
import { almTypeInfo } from "@/model/baseTypes";
import type { BoardCard } from "@/model/card";
import { isArtLevelWorkitem } from "@/model/stickyType";
import { useGlobalI18n } from "@/translations/i18n";
import { asReadonly } from "@/utils/vue";

import { useBoardStore } from "./board";
import { toggled } from "./helpers/toggle";
import { useLinkStore } from "./link";
import { useSessionStore } from "./session";
import { useStickyTypeStore } from "./stickyType";
import { useTeamStore } from "./team";

const { t } = useGlobalI18n();

export interface IssueSelectorJQLQuerySection {
  name: string;
  value: string;
  isDefault: boolean;
}

interface IssueSelectorJQLQuery {
  sections: IssueSelectorJQLQuerySection[];
  separator: string;
}

export interface LinkedToItem {
  id: string;
  almId: string;
  text: string;
  type: { color: Color };
  teamId?: string;
  teamName: string;
}

/** Convert a board card to a LinkedToItem for use in the WIS "linked to" filter */
export function boardCardToLinkedToItem(card: BoardCard): LinkedToItem {
  const { almId, text, type, teamId } = card.data;
  const team = teamId ? useTeamStore().findTeam({ id: teamId }) : undefined;
  return {
    id: card.data.id,
    almId: almId!,
    text,
    type: { color: type.color },
    teamId: teamId ?? undefined,
    teamName: team?.name ?? "",
  };
}

export const useIssueSelectorStore = defineStore(
  "issueSelector",
  () => {
    const active = ref(false);
    const issues = ref<ALMIssue[]>([]);
    const totalCount = ref(0);
    const limit = ref(0);
    const isLoading = ref(false);
    const text = ref<string | null>(null);
    const selectedTypeIds = ref<string[]>([]);
    const selectedAssigneeIds = ref<string[]>([]); // pip user IDs (special ID "no" = unassigned, backend translates to ALM IDs)
    const query = ref<IssueSelectorJQLQuery | null>(null);
    const lastReloadedAt = ref<Date | null>(null);
    const selectedLinkedToItems = ref<LinkedToItem[]>([]);

    /**
     * Returns backlog workitem item types which can be mirrored to the team board
     */
    const backlogItemTypes = computed(() =>
      useStickyTypeStore().stickyTypes.filter(
        (type) =>
          type.functionality === "workitem" &&
          type.origin === "backlog" &&
          type.usable.includes("team"),
      ),
    );

    const hasActiveFilter = computed(
      () =>
        !!text.value ||
        !!selectedTypeIds.value.length ||
        !!selectedAssigneeIds.value.length ||
        !!selectedLinkedToItems.value.length,
    );

    const linkedToAlmIds = computed(() => selectedLinkedToItems.value.map((item) => item.almId));

    const showAlmItemsSection = computed(() => {
      const almType = useSessionStore().almType;
      return almTypeInfo[almType].hasIssueSelector;
    });

    /**
     * The ALM Issue Selector is disabled when the team doesn't have a useable project mapping
     * - no project
     * - multiple projects (not yet supported)
     *
     * Returns a message explaining to the user why
     */
    const disabledMessage = computed(() => {
      switch (useBoardStore().board?.almSources.length) {
        case 0:
        case undefined:
          return t("issueSelector.disabled.noSource");
        case 1:
          // The case we support
          return undefined;
        default:
          // TODO: Remove when multi-project support is added
          return t("issueSelector.disabled.multipleSources");
      }
    });

    /**
     * Returns true if the issue selector is disabled (eg. if there's no alm mapping for the current board)
     */
    const disabled = computed(() => !!disabledMessage.value);

    async function fetchIssues() {
      // If the issue selector is disabled, don't fetch anything
      if (disabled.value) {
        reset();
        return;
      }

      const sessionId = useSessionStore().current.id;
      const teamId = useTeamStore().current.id;

      if (!sessionId || !teamId) {
        return;
      }

      isLoading.value = true;
      query.value = null;

      try {
        // Convert sticky type IDs to ALM issue type IDs (e.g., Jira issue type IDs)
        const almTypeIds = useStickyTypeStore().getAlmTypeIds(selectedTypeIds.value);

        const response = await fetchAlmIssues(teamId, sessionId, {
          text: text.value || undefined,
          almTypeIds,
          assigneeIds: selectedAssigneeIds.value,
          linkedTo: linkedToAlmIds.value,
        });
        issues.value = response.results;
        totalCount.value = response.total;
        limit.value = response.limit;
        query.value = response.query || null;
        lastReloadedAt.value = new Date();
      } catch (err) {
        captureException(err);
      } finally {
        isLoading.value = false;
      }
    }

    function toggle(activate?: boolean) {
      if (toggled(active, activate)) {
        active.value = true;
        if (showAlmItemsSection.value) {
          const pinnedCards = useLinkStore().markingCardsLinkedCards;
          const pinnedArtCard = pinnedCards?.find(
            (c) => isArtLevelWorkitem(c.data) && c.data.almId,
          );
          if (pinnedArtCard) {
            selectedLinkedToItems.value = [boardCardToLinkedToItem(pinnedArtCard)];
          }
          void fetchIssues();
        }
      } else {
        active.value = false;
        reset();
      }
    }

    function reset() {
      // Reset all state (used on close and session change)
      issues.value = [];
      totalCount.value = 0;
      limit.value = 0;
      query.value = null;
      lastReloadedAt.value = null;
      resetFilters();
      useLinkStore().removeAllMarks();
    }

    function resetFilters() {
      text.value = null;
      selectedTypeIds.value = [];
      selectedAssigneeIds.value = [];
      selectedLinkedToItems.value = [];
    }

    function resetFiltersAndFetch() {
      resetFilters();
      void fetchIssues();
    }

    function removeIssue(issueId: string) {
      const index = issues.value.findIndex((issue) => issue.issueId === issueId);
      if (index !== -1) {
        issues.value.splice(index, 1);
        if (totalCount.value > 0) {
          totalCount.value--;
        }
      }
    }

    return {
      text: asReadonly(text),
      query: asReadonly(query),
      active: asReadonly(active),
      isLoading: asReadonly(isLoading),
      lastReloadedAt: asReadonly(lastReloadedAt),
      selectedTypeIds: asReadonly(selectedTypeIds),
      selectedAssigneeIds: asReadonly(selectedAssigneeIds),
      selectedLinkedToItems: asReadonly(selectedLinkedToItems),
      totalCount: asReadonly(totalCount),
      limit: asReadonly(limit),
      issues: asReadonly(issues),

      backlogItemTypes,
      showAlmItemsSection,
      linkedToAlmIds,
      disabled,
      disabledMessage,
      hasActiveFilter,

      toggle,
      setText: (s: string) => (text.value = s.trim() || null),
      setSelectedTypeIds: (ids: string[]) => (selectedTypeIds.value = ids),
      setSelectedAssigneeIds: (ids: string[]) => (selectedAssigneeIds.value = ids),
      setSelectedLinkedToItems: (items: LinkedToItem[]) => (selectedLinkedToItems.value = items),
      fetchIssues,
      resetFiltersAndFetch,
      removeIssue,
    };
  },
  { autoReset: true },
);
